// NarrowSidebar — quick-access cubes pinned immediately to the right
// of the main sidebar. Independent floating cards (Book In, Receipt,
// Quote, Lookup), stacked vertically and pinned to the vertical centre
// of the viewport. There is no shared rail panel; each cube is its own
// rounded card with its own background.
//
// The trailing "Skip" cube is a quick-action entry point into the Skip
// module — unlike the others it does NOT navigate; it opens the Skip
// quick-actions chooser modal (owned by CollectionsLayout, passed in via
// `onSkipQuickAction`). It only renders for staff/manager/admin roles
// (operators are kiosk-only) and behind the Skip SubscriptionGate.
//
// Settings lives in the main sidebar (CollectionsNav), not here.

import { type ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import { CalendarCheck, Calculator, Container, ReceiptText, Search } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useAuthStore } from '../../store/authStore';
import { SubscriptionGate } from '../SubscriptionGate/SubscriptionGate';
import type { UserSummary } from '../../types/api';
import styles from './NarrowSidebar.module.css';

interface QuickItem {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
  end?: boolean;
}

const ITEMS: QuickItem[] = [
  { to: '/collections/bookings/create', label: 'Book In', icon: CalendarCheck },
  { to: '/collections/receipts/new', label: 'RECEIPT', icon: ReceiptText },
  { to: '/collections/quote', label: 'Quote', icon: Calculator },
  { to: '/collections/lookup', label: 'Lookup', icon: Search },
];

// Roles permitted to use the Skip quick actions. Mirrors the server's
// staff/manager/admin gate on the skip endpoints; weighbridge_operator
// (kiosk-only) and driver are deliberately excluded.
const SKIP_QUICK_ACTION_ROLES: ReadonlyArray<UserSummary['role']> = [
  'staff',
  'manager',
  'admin',
];

export interface NarrowSidebarProps {
  /** When provided AND the current role may use Skip, a trailing "Skip"
   *  cube renders that invokes this handler (opens the chooser modal)
   *  instead of navigating. */
  onSkipQuickAction?: () => void;
}

function Cube({ item }: { item: QuickItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) => cn(styles.item, isActive && styles.itemActive)}
    >
      <span className={styles.icon}>
        <item.icon size={22} aria-hidden />
      </span>
      <span className={styles.label}>{item.label}</span>
    </NavLink>
  );
}

function SkipCube({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className={cn(styles.item, styles.itemButton)}
      onClick={onClick}
    >
      <span className={styles.icon}>
        <Container size={22} aria-hidden />
      </span>
      <span className={styles.label}>Skip</span>
    </button>
  );
}

export function NarrowSidebar({ onSkipQuickAction }: NarrowSidebarProps = {}): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const showSkip =
    onSkipQuickAction !== undefined &&
    role !== undefined &&
    SKIP_QUICK_ACTION_ROLES.includes(role);

  return (
    <nav className={styles.rail} aria-label="Quick access">
      {ITEMS.map((item) => (
        <Cube key={item.to} item={item} />
      ))}
      {showSkip ? (
        <SubscriptionGate feature="skip">
          <SkipCube onClick={onSkipQuickAction} />
        </SubscriptionGate>
      ) : null}
    </nav>
  );
}
